import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    ListSubheader,
} from "@mui/material";
import { Button } from "@foodpilot/foods";
import { TextField } from "@mui/material";
import { EXTENSION_POINTS, type CreatePuckExtensionRequest } from "src/components/PuckExtensions";

interface CreateDynaBlockDialogProps {
    open: boolean;
    onClose: () => void;
    onCreateDynaBlock: (data: CreatePuckExtensionRequest) => void;
    isCreating: boolean;
}

interface DynaBlockFormData {
    name: string;
    extensionPoint: string;
    priority: number;
    status: "active" | "inactive" | "draft";
}

// Génération dynamique des options à partir de EXTENSION_POINTS
const generateExtensionPointOptions = () => {
    return Object.entries(EXTENSION_POINTS)
        .map(([key, value]) => ({
            value,
            label: formatExtensionPointLabel(key, value),
            category: getExtensionPointCategory(value),
        }))
        .sort((a, b) => {
            // Tri par catégorie puis par label
            if (a.category !== b.category) {
                return a.category.localeCompare(b.category);
            }
            return a.label.localeCompare(b.label);
        });
};

const formatExtensionPointLabel = (key: string, value: string): string => {
    // Conversion automatique du nom de clé en libellé lisible
    return key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const getExtensionPointCategory = (value: string): string => {
    const [category] = value.split(".");
    return category.charAt(0).toUpperCase() + category.slice(1);
};

const EXTENSION_POINT_OPTIONS = generateExtensionPointOptions();

export const CreateDynaBlockDialog = ({ open, onClose, onCreateDynaBlock, isCreating }: CreateDynaBlockDialogProps) => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState<DynaBlockFormData>({
        name: "",
        extensionPoint: "",
        priority: 1,
        status: "draft",
    });

    const [errors, setErrors] = useState<Partial<DynaBlockFormData>>({});

    const handleClose = () => {
        if (!isCreating) {
            setFormData({
                name: "",
                extensionPoint: "",
                priority: 1,
                status: "draft",
            });
            setErrors({});
            onClose();
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<DynaBlockFormData> = {};

        if (!formData.name.trim()) {
            newErrors.name = t("settings.cms.dynablocks.validation.name_required", "Name is required");
        }

        if (!formData.extensionPoint) {
            newErrors.extensionPoint = t(
                "settings.cms.dynablocks.validation.extension_point_required",
                "Extension point is required",
            );
        }

        if (formData.priority < 1 || formData.priority > 999) {
            newErrors.priority = t(
                "settings.cms.dynablocks.validation.priority_range",
                "Priority must be between 1 and 999",
            ) as any;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onCreateDynaBlock(formData);
        }
    };

    const updateFormData = (field: keyof DynaBlockFormData, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t("settings.cms.dynablocks.create_dialog.title", "Create New DynaBlock")}</DialogTitle>

            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <TextField
                        label={t("settings.cms.dynablocks.create_dialog.name", "DynaBlock Name")}
                        value={formData.name}
                        onChange={(e) => updateFormData("name", e.target.value)}
                        error={!!errors.name}
                        helperText={errors.name}
                        placeholder={t("settings.cms.dynablocks.create_dialog.name_placeholder", "e.g. Welcome Banner")}
                        fullWidth
                        disabled={isCreating}
                    />

                    <FormControl fullWidth error={!!errors.extensionPoint} disabled={isCreating}>
                        <InputLabel>
                            {t("settings.cms.dynablocks.create_dialog.extension_point", "Extension Point")}
                        </InputLabel>
                        <Select
                            value={formData.extensionPoint}
                            onChange={(e) => updateFormData("extensionPoint", e.target.value)}
                            label={t("settings.cms.dynablocks.create_dialog.extension_point", "Extension Point")}
                        >
                            {(() => {
                                const grouped = EXTENSION_POINT_OPTIONS.reduce(
                                    (acc, option) => {
                                        if (!acc[option.category]) {
                                            acc[option.category] = [];
                                        }
                                        acc[option.category].push(option);
                                        return acc;
                                    },
                                    {} as Record<string, typeof EXTENSION_POINT_OPTIONS>,
                                );

                                return Object.entries(grouped)
                                    .map(([category, options]) => [
                                        <ListSubheader key={`header-${category}`} sx={{ fontWeight: "bold" }}>
                                            {category}
                                        </ListSubheader>,
                                        ...options.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        )),
                                    ])
                                    .flat();
                            })()}
                        </Select>
                        {errors.extensionPoint && (
                            <div style={{ color: "red", fontSize: "0.75rem", marginTop: "3px" }}>
                                {errors.extensionPoint}
                            </div>
                        )}
                    </FormControl>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label={t("settings.cms.dynablocks.create_dialog.priority", "Priority")}
                            type="number"
                            value={formData.priority.toString()}
                            onChange={(e) => updateFormData("priority", parseInt(e.target.value) || 1)}
                            error={!!errors.priority}
                            helperText={
                                errors.priority ||
                                t("settings.cms.dynablocks.create_dialog.priority_help", "Higher numbers appear first")
                            }
                            inputProps={{ min: 1, max: 999 }}
                            disabled={isCreating}
                            sx={{ flex: 1 }}
                        />

                        <FormControl sx={{ flex: 1 }} disabled={isCreating}>
                            <InputLabel>{t("settings.cms.dynablocks.create_dialog.status", "Status")}</InputLabel>
                            <Select
                                value={formData.status}
                                onChange={(e) => updateFormData("status", e.target.value)}
                                label={t("settings.cms.dynablocks.create_dialog.status", "Status")}
                            >
                                <MenuItem value="draft">{t("settings.cms.dynablocks.status.draft", "Draft")}</MenuItem>
                                <MenuItem value="active">
                                    {t("settings.cms.dynablocks.status.active", "Active")}
                                </MenuItem>
                                <MenuItem value="inactive">
                                    {t("settings.cms.dynablocks.status.inactive", "Inactive")}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="text" onClick={handleClose} disabled={isCreating}>
                    {t("common.cancel", "Cancel")}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isCreating || !formData.name.trim() || !formData.extensionPoint}
                    startIcon={isCreating ? <CircularProgress size={16} /> : undefined}
                >
                    {isCreating ?
                        t("settings.cms.dynablocks.create_dialog.creating", "Creating...")
                    :   t("settings.cms.dynablocks.create_dialog.create", "Create DynaBlock")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
